import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, mergeProps as _mergeProps, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, unref as _unref, normalizeClass as _normalizeClass, createBlock as _createBlock, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { style: {"display":"inline-block"} }
const _hoisted_2 = {
  key: 0,
  class: "d-flex justify-center align-center py-8"
}
const _hoisted_3 = {
  key: 1,
  class: "text-center py-8"
}
const _hoisted_4 = { class: "text-body-1 text-grey-darken-1 font-weight-medium" }
const _hoisted_5 = { key: 2 }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "d-flex" }
const _hoisted_8 = { class: "flex-grow-1 pa-3" }
const _hoisted_9 = ["innerHTML"]
const _hoisted_10 = { class: "text-caption text-grey text-right" }
const _hoisted_11 = {
  key: 1,
  class: "loading-text"
}
const _hoisted_12 = {
  key: 0,
  class: "text-center py-3 text-caption text-grey"
}

import { ref, onMounted, computed, nextTick, onBeforeUnmount } from "vue";
import Auth from "@/API/Auth";
import echo from "@/plugins/echo";
import NotificationAPI from "@/API/notification/notification";
import RoleApi from "@/API/Role/Role";
import { filter } from "jszip";
import emitter from "@/plugins/eventBus";

interface Notification {
  id: string;
  description: string;
  created_at: string;
  read_at: string | null;
  type: string | string[];
  user_from: string | null;
  user_to: string[];
  updated_at: string;
  class: string;
  classId: string;
  readers: string[];
  roles: any[];
  teams: any[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'notificationBox',
  setup(__props) {

const USER_ID = Auth.USER.id;
const notifications = ref<Notification[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const showNotifications = ref(false);
const hasInitiallyLoaded = ref(false);

// Pagination state
const currentPage = ref(1);
const perPage = ref(10);
const totalRecords = ref(0);
const hasMorePages = ref(false);

// Store all notifications for client-side pagination
const allNotifications = ref<Notification[]>([]);

// Refs
const notificationContainer = ref<HTMLElement | null>(null);
const loadMoreTrigger = ref<HTMLElement | null>(null);
const observer = ref<IntersectionObserver | null>(null);

const notificationApi = new NotificationAPI();
const roleApi = new RoleApi();

const unreadCount = computed(() => {
  return allNotifications.value.filter((n) => !n.readers?.includes(USER_ID))
    .length;
});

const fetchNotifications = async (reset = false) => {
  // Check if user is authenticated
  if (!USER_ID || !Auth.USER?.id) {
    console.warn("User not authenticated, skipping notification fetch");
    return;
  }

  // Prevent multiple simultaneous requests
  if (loading.value) return;

  // Reset pagination if needed
  if (reset) {
    currentPage.value = 1;
    notifications.value = [];
    allNotifications.value = [];
    hasInitiallyLoaded.value = false;
  }

  loading.value = true;
  error.value = null;

  try {
    // Fetch all notifications with original 3 parameters
    const response = await notificationApi.getUserNotifications(
      USER_ID,
      Auth.ROLE.id,
      Auth.USER?.team_ids
    );

    // Extract notifications from response
    const fetchedNotifications = response.data.notifications ?? response.data;
    
    // Store all notifications only on first fetch
    if (!hasInitiallyLoaded.value || reset) {
      allNotifications.value = Array.isArray(fetchedNotifications) ? fetchedNotifications : [];
      totalRecords.value = allNotifications.value.length;
      hasInitiallyLoaded.value = true;
    }
    
    // Calculate pagination on frontend
    const startIndex = (currentPage.value - 1) * perPage.value;
    const endIndex = startIndex + perPage.value;
    const paginatedNotifications = allNotifications.value.slice(startIndex, endIndex);
    
    if (reset || currentPage.value === 1) {
      notifications.value = paginatedNotifications;
    } else {
      notifications.value = [...notifications.value, ...paginatedNotifications];
    }
    
    // Check if there are more pages
    hasMorePages.value = endIndex < allNotifications.value.length;

  } catch (err: any) {
    console.error("Error fetching notifications:", err);
    error.value = err.message || "Unknown error";
  } finally {
    loading.value = false;
  }
};

const loadMore = () => {
  if (hasMorePages.value && !loading.value) {
    currentPage.value++;
    fetchNotifications();
  }
};

const setupIntersectionObserver = () => {
  nextTick(() => {
    if (loadMoreTrigger.value && notificationContainer.value) {
      // Disconnect existing observer if any
      if (observer.value) {
        observer.value.disconnect();
      }

      observer.value = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadMore();
          }
        },
        {
          root: notificationContainer.value,
          threshold: 0.1,
        }
      );

      observer.value.observe(loadMoreTrigger.value);
    }
  });
};

const handleScroll = () => {
  // Optional: Alternative scroll-based loading
  const container = notificationContainer.value;
  if (!container || loading.value || !hasMorePages.value) return;

  const scrollPosition = container.scrollTop + container.clientHeight;
  const scrollHeight = container.scrollHeight;

  // Load more when scrolled to 90% of the content
  if (scrollPosition >= scrollHeight * 0.9) {
    loadMore();
  }
};

const toggleNotifications = async () => {
  if (showNotifications.value) {
    // Menu is closing, no need to fetch
    return;
  }
  
  // Menu is opening, fetch notifications if not loaded
  if (!hasInitiallyLoaded.value) {
    await fetchNotifications(true);
    setupIntersectionObserver();
  }
};

const markAsRead = async (id: string) => {
  try {
    const notificationToMark = notifications.value.find(
      (n) => n.id === id && !n.readers?.includes(USER_ID)
    );

    if (notificationToMark) {
      await notificationApi.markAsRead(id, USER_ID);

      // Update both arrays
      notifications.value = notifications.value.map((notification) => {
        if (notification.id === id && !notification.readers?.includes(USER_ID)) {
          return {
            ...notification,
            readers: [...(notification.readers || []), USER_ID],
          };
        }
        return notification;
      });

      allNotifications.value = allNotifications.value.map((notification) => {
        if (notification.id === id && !notification.readers?.includes(USER_ID)) {
          return {
            ...notification,
            readers: [...(notification.readers || []), USER_ID],
          };
        }
        return notification;
      });
    }
  } catch (err) {
    console.error("Error marking as read:", err);
  }
};

const getNotificationTypeClass = (type: string | string[]) => {
  const t = Array.isArray(type) ? type[0] : type;
  return (
    {
      mail: "border-primary",
      system: "border-error",
      auto: "border-success",
    }[t] || "border-grey"
  );
};

const formatTime = (timestamp: string): string => {
  const now = new Date();
  const diffInSec = Math.floor(
    (now.getTime() - new Date(timestamp).getTime()) / 1000
  );

  if (diffInSec < 60) return "Just now";
  if (diffInSec < 3600) return `${Math.floor(diffInSec / 60)} min`;
  if (diffInSec < 86400) return `${Math.floor(diffInSec / 3600)} hr`;
  return `${Math.floor(diffInSec / 86400)} day${
    diffInSec >= 172800 ? "s" : ""
  }`;
};

const addNotificationIfNew = (n: Notification) => {
  const exists = notifications.value.some((existing) => existing.id === n.id);
  if (!exists) {
    // Add to both displayed and all notifications
    notifications.value.unshift(n);
    allNotifications.value.unshift(n);
    totalRecords.value++;
  }
};

onMounted(async () => {
  // Load notifications in background on mount
  await fetchNotifications(true);
  setupIntersectionObserver();

  emitter.on("markeAsRead", () => {
    fetchNotifications(true).then(() => {
      setupIntersectionObserver();
    });
  });

  // Listen to the User channel
  try {
    echo.private(`notification.${USER_ID}`).listen(".Alert", (event: any) => {
      const n = event.notification;
      addNotificationIfNew({
        id: n.id,
        description: n.description,
        created_at: n.created_at,
        read_at: null,
        type: n.type,
        user_from: null,
        user_to: n.user_to,
        updated_at: n.updated_at,
        class: n.class,
        classId: n.classId,
        readers: n.readers || [],
        roles: n.roles,
        teams: n.teams,
      });
    });

    // Listen to the Role channel
    echo.private(`role.${Auth.ROLE.id}`).listen(".Alert", (event: any) => {
      const n = event.notification;
      addNotificationIfNew({
        id: n.id,
        description: n.description,
        created_at: n.created_at,
        read_at: null,
        type: n.type,
        user_from: null,
        user_to: n.user_to,
        updated_at: n.updated_at,
        class: n.class,
        classId: n.classId,
        readers: n.readers || [],
        roles: n.roles,
        teams: n.teams,
      });
    });

    // Listen to the Team channel
    Auth.USER?.team_ids?.forEach((teamId: any) => {
      echo.private(`team.${teamId}`).listen(".Alert", (event: any) => {
        const n = event.notification;
        addNotificationIfNew({
          id: n.id,
          description: n.description,
          created_at: n.created_at,
          read_at: null,
          type: n.type,
          user_from: null,
          user_to: n.user_to,
          updated_at: n.updated_at,
          class: n.class,
          classId: n.classId,
          readers: n.readers || [],
          roles: n.roles,
          teams: n.teams,
        });
      });
    });
  } catch (err) {
    console.log(err);
  }
});

onBeforeUnmount(() => {
  if (observer.value) {
    observer.value.disconnect();
  }
});

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_badge = _resolveComponent("v-badge")!
  const _component_btn = _resolveComponent("btn")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_router_link = _resolveComponent("router-link")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_menu = _resolveComponent("v-menu")!

  return (_openBlock(), _createBlock(_component_v_menu, {
    flat: "",
    modelValue: showNotifications.value,
    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showNotifications).value = $event)),
    "close-on-content-click": false,
    "max-width": "350"
  }, {
    activator: _withCtx(({ props }) => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_component_btn, _mergeProps(props, {
          icon: "",
          color: "grey-darken-1",
          class: "no-hover-effect",
          style: {"transition":"none !important","background-color":"transparent !important","pointer-events":"auto"},
          onClick: toggleNotifications,
          onMouseenter: _cache[0] || (_cache[0] = ($event: any) => ($event.target.style.backgroundColor = 'transparent')),
          onMouseleave: _cache[1] || (_cache[1] = ($event: any) => ($event.target.style.backgroundColor = 'transparent'))
        }), {
          default: _withCtx(() => [
            _createVNode(_component_v_badge, {
              content: unreadCount.value > 99 ? '+99' : unreadCount.value,
              "model-value": unreadCount.value > 0,
              color: "error",
              class: "custom-badge"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, null, {
                  default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                    _createTextVNode("mdi-bell-outline", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["content", "model-value"])
          ]),
          _: 1
        }, 16)
      ])
    ]),
    default: _withCtx(() => [
      _createVNode(_component_v_card, {
        "min-width": "350",
        "max-width": "350",
        elevation: "8"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_title, { class: "text-h6 font-weight-medium px-4 py-3" }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t("notification.notification")), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_v_divider),
          _createElementVNode("div", {
            ref_key: "notificationContainer",
            ref: notificationContainer,
            style: {"max-height":"400px","overflow-y":"auto"},
            onScroll: handleScroll
          }, [
            (loading.value && currentPage.value === 1)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  _createVNode(_component_v_progress_circular, {
                    indeterminate: "",
                    color: "primary",
                    size: "40"
                  })
                ]))
              : (error.value || (notifications.value.length === 0 && !loading.value))
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _createVNode(_component_v_icon, {
                      size: "48",
                      color: "grey-lighten-1",
                      class: "mb-2"
                    }, {
                      default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                        _createTextVNode(" mdi-bell-off-outline ", -1)
                      ]))]),
                      _: 1
                    }),
                    _createElementVNode("div", _hoisted_4, _toDisplayString(_ctx.$t("notification.you_do_not_have_notifications")), 1)
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(notifications.value, (item) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: item.id,
                        class: "text-decoration-none",
                        onClick: ($event: any) => (markAsRead(item.id))
                      }, [
                        _createVNode(_component_v_card, {
                          flat: "",
                          class: _normalizeClass([
                'notification-item ma-2 rounded-lg',
                { 'unread-notification': !item.readers?.includes(_unref(USER_ID)) },
              ]),
                          ripple: true,
                          hover: ""
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_7, [
                              _createElementVNode("div", {
                                class: _normalizeClass(["notification-border align-self-stretch", getNotificationTypeClass(item.type)])
                              }, null, 2),
                              _createElementVNode("div", _hoisted_8, [
                                _createElementVNode("div", {
                                  class: "text-body-2 text-grey-darken-3 mb-1",
                                  innerHTML: item.description
                                }, null, 8, _hoisted_9),
                                _createElementVNode("div", _hoisted_10, _toDisplayString(formatTime(item.created_at)), 1)
                              ])
                            ])
                          ]),
                          _: 2
                        }, 1032, ["class"])
                      ], 8, _hoisted_6))
                    }), 128)),
                    _withDirectives(_createElementVNode("div", {
                      ref_key: "loadMoreTrigger",
                      ref: loadMoreTrigger,
                      class: "load-more-trigger py-3"
                    }, [
                      (loading.value)
                        ? (_openBlock(), _createBlock(_component_v_progress_circular, {
                            key: 0,
                            indeterminate: "",
                            color: "primary",
                            size: "24"
                          }))
                        : (_openBlock(), _createElementBlock("span", _hoisted_11, "Loading more..."))
                    ], 512), [
                      [_vShow, hasMorePages.value]
                    ]),
                    (!hasMorePages.value && notifications.value.length > 0)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_12, _toDisplayString(_ctx.$t("notification.noMoreNotifications") || "No more notifications"), 1))
                      : _createCommentVNode("", true)
                  ]))
          ], 544),
          _createVNode(_component_v_card_actions, { class: "justify-center py-2" }, {
            default: _withCtx(() => [
              _createVNode(_component_router_link, {
                to: { name: 'AllNotification' },
                class: "text-decoration-none"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    variant: "text",
                    color: "primary",
                    size: "small",
                    class: "text-capitalize"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t("notification.viewAll")), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})